import {
  Alert,
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  DropdownItem,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDropdown,
  Separator,
  showNotification,
  Tooltip,
} from 'platform/components';
import {Grid, HStack, Show, Space, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotString, isString} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {
  GetArticleResponse,
  PostArticleRequest,
  useGetDefaultTreeFolderQuery,
  useGetDispensingUnitPresetsQuery,
  useGetHandlingUnitsQuery,
  useGetWarehouseQuery,
  useGetWarehousesQuery,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
  useLazyGetArticleQuery,
  useLazyGetWarehouseQuery,
  usePostArticleMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  getActiveManufacturerOptions,
  getActiveOptionsFromHandlingUnits,
  getDefaultOptionFromHandlingUnits,
  getOptionsFromWarehouses,
  handleApiError,
  useDuplicateErrorHandler,
} from '@dms/shared';

import {
  AllOrNone,
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
  useOnMount,
} from 'shared/web';

import {ArticleCreateTreeFolderPath} from './components/ArticleCreateTreeFolderPath/ArticleCreateTreeFolderPath';
import {ArticleCreateForm, ArticleCreateFormSchema} from './types/ArticleCreateForm';
import {ArticleState} from './types/ArticleState';

const FOLDER_CONTEXT_TARGET = 'spare-parts-catalog';

type SubmitButtons = 'CREATE_AND_EDIT' | 'CREATE' | 'ADD' | 'CREATE_AND_REPLACE';

export type ArticleCreateDefaultValues = {
  catalogNumber?: string;
  manufacturerId?: string;
  warehouseId?: string;
  name?: string;
  activeFolderId?: string;
};

export type CreatedArticleData = {
  formValues: ArticleCreateForm;
  articleId: string;
  submittedButton: SubmitButtons;
};

export type HandleCheckItemConfig = {
  manufacturerId: string;
  catalogNumber: string;
  warehouseId: string;
};

export type BasketProps = AllOrNone<{
  hasAddToBasketButton: boolean;
  onAddToBasket: (articleId: string) => void;
}>;

export type ArticleCreateProps = {
  onClose?: () => void;
  onArticleCreate?: (createdArticleData: CreatedArticleData) => void;
  defaultValues?: ArticleCreateDefaultValues;
  hasCreateAndEditButton?: boolean;
  hasCreateAndReplaceButton?: boolean;
  submitButtonTitle?: string;
  isWarehouseDisabled?: boolean;
  supplierId?: string;
} & RequiredTestIdProps &
  BasketProps;

export function ArticleCreate(props: ArticleCreateProps) {
  const navigate = useNavigate();

  const {duplicateError, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [articleId, setArticleId] = useState<string | Nullish>(null);

  const [prefilledManufacturerId, setPrefilledManufacturerId] = useState<string | undefined>(
    props.defaultValues?.manufacturerId
  );

  const [activeFolderId, setActiveFolderId] = useState(props.defaultValues?.activeFolderId);
  const [articleState, setArticleState] = useState<ArticleState | null>(null);
  const [lastCheckedState, setLastCheckedState] = useState<HandleCheckItemConfig | null>(null);
  const [loadedArticle, setLoadedArticle] = useState<GetArticleResponse | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [supplierPriceListData, setSupplierPriceListData] = useState<{
    name: string | null;
    measurementUnitId: string | null;
  } | null>(null);

  const hasPopulatedFormRef = useRef(false);
  const hasPopulatedPriceListDataRef = useRef(false);

  const articleIdRef = useRef<string | null>(null);
  const clickedButtonRef = useRef<SubmitButtons | null>(null);

  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: warehouse,
    isLoading: isWarehouseLoading,
    isError: hasWarehouseError,
  } = useGetWarehouseQuery(
    {warehouseId: props.defaultValues?.warehouseId as string},
    {
      skip:
        isNotString(props.defaultValues?.warehouseId) ||
        isNilOrEmpty(props.defaultValues?.warehouseId),
    }
  );

  const {
    data: handlingUnits,
    isLoading: isHandlingUnitsLoading,
    isError: isHandlingUnitsError,
  } = useGetHandlingUnitsQuery();

  const {
    data: manufacturers,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: defaultTreeFolder,
    isLoading: isDefaultTreeFolderLoading,
    isError: isDefaultTreeFolderError,
  } = useGetDefaultTreeFolderQuery(
    {contextTarget: FOLDER_CONTEXT_TARGET},
    {skip: isNotNil(activeFolderId)}
  );

  if (isNil(activeFolderId) && isNotNil(defaultTreeFolder?.treeFolder?.id)) {
    setActiveFolderId(defaultTreeFolder?.treeFolder?.id);
  }

  if (isNil(prefilledManufacturerId) && isNotNil(warehouse?.defaultManufacturerId)) {
    setPrefilledManufacturerId(warehouse?.defaultManufacturerId);
  }

  const [createArticle, {isLoading: isCreateArticleLoading}] = usePostArticleMutation();

  const [getArticleIdByManufacturerNumberAndManufacturerId, {isFetching: isArticleFetching}] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const [getArticle, {isLoading: isArticleDataFetching}] = useLazyGetArticleQuery();

  const [getWarehouse, {isLoading: isWarehouseFetching}] = useLazyGetWarehouseQuery();

  const {
    data: dispensingUnitPresets,
    isLoading: isDispensingUnitPresetsLoading,
    isError: isDispensingUnitPresetsError,
  } = useGetDispensingUnitPresetsQuery();

  const hasCreateAndEditButton = props.hasCreateAndEditButton ?? false;

  const doesExist = articleState === 'doesExist';
  const doesNotExist = articleState === 'doesNotExist';

  const isLoading =
    isWarehousesLoading ||
    isHandlingUnitsLoading ||
    isManufacturersLoading ||
    isDefaultTreeFolderLoading ||
    isDispensingUnitPresetsLoading ||
    isArticleDataFetching;

  const isError =
    isWarehousesError ||
    hasWarehouseError ||
    isHandlingUnitsError ||
    isManufacturersError ||
    isDefaultTreeFolderError ||
    isDispensingUnitPresetsError;

  useOnMount(() => {
    if (
      isString(props.defaultValues?.manufacturerId) &&
      isString(props.defaultValues?.catalogNumber) &&
      isString(props.defaultValues?.warehouseId)
    ) {
      // eslint-disable-next-line react-hooks/immutability
      handleCheckItem({
        manufacturerId: props.defaultValues?.manufacturerId,
        catalogNumber: props.defaultValues?.catalogNumber,
        warehouseId: props.defaultValues?.warehouseId,
      });
    }
  });

  const activeManufacturerOptions = getActiveManufacturerOptions(manufacturers);

  const handleSubmit: FormSubmitHandler<ArticleCreateForm> = async (values, setErrors) => {
    if (isNil(activeFolderId)) {
      return setErrors([
        {
          name: 'activeFolderId',
          message: i18n.t('errors.field_is_required'),
        },
      ]);
    }

    if (clickedButtonRef.current === 'ADD') {
      return handleAddToBasket();
    }

    const request: PostArticleRequest = {
      warehouseId: values.warehouseId,
      body: {
        manufacturerNumber: values.catalogNumber,
        manufacturerId: values.manufacturerId,
        name: values.name,
        handlingUnit: values.handlingUnit,
        storageLocation: values.storageLocation,
        dispensingUnit: values.dispensingUnit,
        treeFolder: {leafId: activeFolderId},
      },
    };

    await createArticle(request)
      .unwrap()
      .then((article) => {
        const createdArticleData: CreatedArticleData = {
          formValues: values,
          articleId: article?.articleId,
          submittedButton: clickedButtonRef.current as SubmitButtons,
        };

        props.onArticleCreate?.(createdArticleData);
      })
      .then(() => showNotification.success(i18n.t('page.warehouse.notification.articleCreated')))
      .catch(duplicateErrorHandler);
  };

  const handleDetailRedirect = (warehouseId: string) => {
    navigate(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {
          id: articleIdRef.current,
          warehouseId,
        },
        isSearchParamsPreserved: false,
      })
    );
  };

  const handleDetailOpen = (warehouseId: string) => {
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {
          id: articleIdRef.current,
          warehouseId,
        },
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );
  };

  const handleCheckItem = async (config: HandleCheckItemConfig) => {
    const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
      warehouseId: config.warehouseId,
      manufacturerNumber: config.catalogNumber,
      manufacturerId: config.manufacturerId,
      supplierId: props.supplierId,
    })
      .unwrap()
      .then((response) => {
        setLastCheckedState(config);
        setArticleId(response?.articleId);
        return response;
      })
      .catch(handleApiError);

    if (articleIdResponse?.articleId) {
      setArticleState('doesExist');
      articleIdRef.current = articleIdResponse.articleId;

      await getArticle({
        warehouseId: config.warehouseId,
        articleId: articleIdResponse.articleId,
      })
        .unwrap()
        .then((article) => {
          setLoadedArticle(article);
          setIsViewMode(true);

          if (article.treeFolder.leafId) {
            setActiveFolderId(article.treeFolder.leafId);
          }
        })
        .catch(handleApiError);
    } else {
      setArticleState('doesNotExist');
      articleIdRef.current = null;
      setLoadedArticle(null);
      setIsViewMode(false);
      hasPopulatedFormRef.current = false;
      hasPopulatedPriceListDataRef.current = false;
      setSupplierPriceListData({
        name: articleIdResponse?.name ?? null,
        measurementUnitId: articleIdResponse?.measurementUnitId ?? null,
      });
    }
  };

  const handleAddToBasket = () => {
    if (!articleId || !props.onAddToBasket) {
      return;
    }

    clickedButtonRef.current = 'ADD';
    props.onAddToBasket(articleId);
  };

  const handleWarehouseChange = (
    manufacturerId: string,
    warehouseId: string | number | null,
    formApi: UseFormReturn<ArticleCreateForm>
  ) => {
    if (isNotString(warehouseId)) {
      return;
    }

    getWarehouse({warehouseId: warehouseId as string})
      .unwrap()
      .then((warehouse) => {
        if (isNotNil(manufacturerId) || isNil(warehouse.defaultManufacturerId)) {
          return;
        }

        if (isNilOrEmpty(formApi.getValues('manufacturerId'))) {
          formApi.setValue('manufacturerId', warehouse.defaultManufacturerId);
        }
      })
      .catch(handleApiError);
  };

  const clearCheckedState = () => {
    setArticleState(null);
    articleIdRef.current = null;
    setLoadedArticle(null);
    setIsViewMode(false);
    hasPopulatedFormRef.current = false;
    hasPopulatedPriceListDataRef.current = false;
    setSupplierPriceListData(null);
  };

  const defaultValues: Partial<ArticleCreateForm> = {
    catalogNumber: props.defaultValues?.catalogNumber,
    manufacturerId: prefilledManufacturerId,
    warehouseId: props.defaultValues?.warehouseId,
    name: props.defaultValues?.name,
    handlingUnit: getDefaultOptionFromHandlingUnits(handlingUnits)[0]?.value,
    activeFolderId,
  };

  const mapArticleToFormValues = (article: GetArticleResponse): Partial<ArticleCreateForm> => {
    return {
      catalogNumber: article.manufacturerNumber,
      manufacturerId: article.manufacturerId,
      warehouseId: article.warehouseId,
      name: article.name,
      handlingUnit: article.handlingUnit,
      storageLocation: article.storageLocation ?? null,
      dispensingUnit: article.dispensingUnit,
      activeFolderId: article.treeFolder.leafId ?? '',
    };
  };

  const submitButtonTitle = props.submitButtonTitle ?? i18n.t('general.actions.create');

  return (
    <>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={!warehouses}
        minHeight={96}
        data-testid={suffixTestId('status', props)}
      >
        <Form<ArticleCreateForm>
          defaultValues={defaultValues}
          experimentalZodSchema={ArticleCreateFormSchema}
          onSubmit={handleSubmit}
        >
          {(control, formApi) => {
            const manufacturerId = formApi.watch('manufacturerId');
            const catalogNumber = formApi.watch('catalogNumber');
            const warehouseId = formApi.watch('warehouseId');

            const isCheckItemButtonDisabled =
              isNilOrEmpty(catalogNumber) ||
              isNilOrEmpty(warehouseId) ||
              isNilOrEmpty(manufacturerId);

            const handleReset = () => {
              formApi.reset();
            };

            const isCurrentStateChecked =
              isNotNil(lastCheckedState) &&
              manufacturerId === lastCheckedState?.manufacturerId &&
              catalogNumber === lastCheckedState?.catalogNumber &&
              warehouseId === lastCheckedState?.warehouseId;

            if (loadedArticle && isViewMode && !hasPopulatedFormRef.current) {
              const formValues = mapArticleToFormValues(loadedArticle);
              formApi.reset(formValues, {keepDefaultValues: false});
              hasPopulatedFormRef.current = true;
            }

            if (doesNotExist && supplierPriceListData && !hasPopulatedPriceListDataRef.current) {
              if (supplierPriceListData.name) {
                formApi.setValue('name', supplierPriceListData.name);
              }
              if (supplierPriceListData.measurementUnitId) {
                formApi.setValue('handlingUnit', supplierPriceListData.measurementUnitId);
              }
              hasPopulatedPriceListDataRef.current = true;
            }

            return (
              <>
                <VStack spacing={4} data-testid={props['data-testid']}>
                  <Show when={duplicateError}>
                    <Alert
                      variant="error"
                      title={i18n.t('entity.warehouse.labels.warehouseArticleAlreadyExists')}
                      hyperlinks={[
                        {
                          size: 'base',
                          title: i18n.t('general.actions.view'),
                          onClick: () => handleDetailRedirect(warehouseId),
                          rightIcon: 'action/open_in_new',
                        },
                      ]}
                      data-testid={suffixTestId('alert.warehouseArticleAlreadyExists', props)}
                    />
                  </Show>
                  <Show when={doesNotExist}>
                    <Alert
                      type="inline"
                      variant="success"
                      title={i18n.t('page.warehouse.labels.articleIsNotInDatabase')}
                      data-testid={suffixTestId('alert.articleIsNotInDatabase', props)}
                    />
                  </Show>
                  <Show when={doesExist}>
                    <Alert
                      type="inline"
                      variant="error"
                      hyperlinks={[
                        {
                          size: 'small',
                          title: i18n.t('general.actions.view'),
                          onClick: () => handleDetailOpen(warehouseId),
                          leftIcon: 'action/open_in_new',
                        },
                      ]}
                      title={i18n.t('entity.warehouse.labels.warehouseArticleAlreadyExists')}
                      data-testid={suffixTestId('alert.articleIsAlreadyInDatabase', props)}
                    />
                  </Show>
                  <Grid columns={1}>
                    <FormField
                      isRequired
                      control={control}
                      name="catalogNumber"
                      type="text"
                      label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                      onChange={clearCheckedState}
                      data-testid={suffixTestId('manufacturerNumber', props)}
                    />
                  </Grid>
                  <Grid columns={2}>
                    <FormField
                      isNotClearable
                      isRequired
                      isLoading={isWarehouseLoading || isWarehouseFetching}
                      control={control}
                      name="manufacturerId"
                      type="choice"
                      label={i18n.t('entity.warehouse.labels.manufacturer')}
                      options={activeManufacturerOptions}
                      data-testid={suffixTestId('manufacturer', props)}
                    />
                    <FormField
                      isNotClearable
                      isRequired
                      isDisabled={props.isWarehouseDisabled}
                      control={control}
                      name="warehouseId"
                      type="choice"
                      label={i18n.t('entity.warehouse.labels.warehouse')}
                      onChange={(warehouseId) => {
                        handleWarehouseChange(manufacturerId, warehouseId, formApi);
                        clearCheckedState();
                      }}
                      options={getOptionsFromWarehouses(warehouses)}
                      data-testid={suffixTestId('warehouseId', props)}
                    />
                  </Grid>
                  <HStack>
                    <Tooltip
                      label={i18n.t('entity.warehouse.labels.currentStateIsChecked')}
                      isDisabled={!isCurrentStateChecked}
                    >
                      <Button
                        variant="secondary"
                        title={i18n.t('general.labels.checkItem')}
                        onClick={() =>
                          handleCheckItem({manufacturerId, catalogNumber, warehouseId})
                        }
                        isLoading={isArticleFetching}
                        isDisabled={isCheckItemButtonDisabled || isCurrentStateChecked}
                        data-testid={suffixTestId('actions.checkItem', props)}
                      />
                    </Tooltip>
                  </HStack>
                  <Separator spacing={0} />
                  <Grid columns={2}>
                    <Tooltip
                      label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                      isDisabled={!isNil(articleState)}
                    >
                      <FormField
                        isRequired
                        isDisabled={isViewMode || isNil(articleState)}
                        control={control}
                        name="name"
                        type="text"
                        label={i18n.t('general.labels.name')}
                        data-testid={suffixTestId('name', props)}
                      />
                    </Tooltip>
                    <Tooltip
                      label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                      isDisabled={!isNil(articleState)}
                    >
                      <FormField
                        isRequired
                        isNotClearable
                        isDisabled={isViewMode || isNil(articleState)}
                        control={control}
                        name="handlingUnit"
                        type="choice"
                        label={i18n.t('entity.warehouse.labels.handlingUnit')}
                        options={getActiveOptionsFromHandlingUnits(handlingUnits)}
                        data-testid={suffixTestId('handlingUnit', props)}
                      />
                    </Tooltip>
                    <Tooltip
                      label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                      isDisabled={!isNil(articleState)}
                    >
                      <FormField
                        isDisabled={isViewMode || isNil(articleState)}
                        control={control}
                        name="storageLocation"
                        type="text"
                        label={i18n.t('entity.warehouse.labels.storageLocation')}
                        data-testid={suffixTestId('storageLocation', props)}
                      />
                    </Tooltip>
                    <Tooltip
                      label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                      isDisabled={!isNil(articleState)}
                    >
                      <FormField
                        isDisabled={isViewMode || isNil(articleState)}
                        control={control}
                        name="dispensingUnit"
                        type="number"
                        label={i18n.t('entity.warehouse.labels.dispensingUnit')}
                        labelActions={
                          dispensingUnitPresets
                            ? [
                                {
                                  title: i18n.t('entity.warehouse.labels.presets'),
                                  onClick: (e) =>
                                    openDropdown(
                                      e,
                                      dispensingUnitPresets?.map((preset) => (
                                        <DropdownItem
                                          key={preset.id}
                                          label={preset.value.toString()}
                                          onClick={() =>
                                            formApi.setValue('dispensingUnit', preset.value)
                                          }
                                        />
                                      ))
                                    ),
                                },
                              ]
                            : undefined
                        }
                        decimalPlaces={4}
                        data-testid={suffixTestId('dispensingUnit', props)}
                      />
                    </Tooltip>
                  </Grid>
                  <ArticleCreateTreeFolderPath
                    isRequired
                    isDisabled={isViewMode}
                    invalidMessage={formApi.formState.errors.activeFolderId?.message}
                    leafId={activeFolderId}
                    onChange={(value) => {
                      setActiveFolderId(value?.id);
                      formApi.setValue('activeFolderId', value?.id ?? '', {shouldValidate: true});
                    }}
                    data-testid={suffixTestId('treeFolderPath', props)}
                  />
                </VStack>
                <DialogFooter>
                  <HStack>
                    <Show when={!isViewMode}>
                      <Button
                        title={i18n.t('general.actions.clear')}
                        leftIcon="content/clear"
                        variant="dangerLink"
                        onClick={handleReset}
                        data-testid={suffixTestId('clear', props)}
                      />
                    </Show>
                    <Space fillAvailable />
                    <ButtonGroup align="right">
                      <Button
                        title={i18n.t('general.actions.discard')}
                        type="reset"
                        variant="secondary"
                        onClick={props.onClose}
                        data-testid={suffixTestId('discard', props)}
                      />
                      <Show when={!isViewMode}>
                        <Show when={props.hasAddToBasketButton && articleId}>
                          <Tooltip
                            label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                            isDisabled={isCurrentStateChecked}
                          >
                            <Button
                              title={i18n.t('general.actions.addToBasket')}
                              variant="primary"
                              onClick={handleAddToBasket}
                              isDisabled={!isCurrentStateChecked}
                              data-testid={suffixTestId('addToBasket', props)}
                            />
                          </Tooltip>
                        </Show>

                        <Show when={hasCreateAndEditButton && !articleId}>
                          <Tooltip
                            label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                            isDisabled={isCurrentStateChecked}
                          >
                            <FormButton
                              control={control}
                              title={i18n.t('entity.warehouse.actions.createAndEditArticle')}
                              type="submit"
                              variant="secondary"
                              onClick={() => (clickedButtonRef.current = 'CREATE_AND_EDIT')}
                              isLoading={isCreateArticleLoading}
                              isDisabled={!isCurrentStateChecked}
                              data-testid={suffixTestId('createAndEditArticle', props)}
                            />
                          </Tooltip>
                        </Show>

                        <Show when={!articleId}>
                          <Tooltip
                            label={i18n.t('entity.warehouse.labels.firstlyCheckItem')}
                            isDisabled={isCurrentStateChecked}
                          >
                            <FormButton
                              control={control}
                              type="submit"
                              title={submitButtonTitle}
                              onClick={() =>
                                (clickedButtonRef.current = props.hasCreateAndReplaceButton
                                  ? 'CREATE_AND_REPLACE'
                                  : 'CREATE')
                              }
                              isLoading={isCreateArticleLoading}
                              isDisabled={!isCurrentStateChecked}
                              data-testid={suffixTestId('create', props)}
                            />
                          </Tooltip>
                        </Show>
                      </Show>

                      <Show when={isViewMode && props.hasCreateAndReplaceButton}>
                        <Button
                          title={i18n.t('general.actions.select')}
                          variant="primary"
                          onClick={() => {
                            if (loadedArticle) {
                              props.onArticleCreate?.({
                                formValues: formApi.getValues(),
                                articleId: loadedArticle.id,
                                submittedButton: 'CREATE_AND_REPLACE',
                              });
                            }
                          }}
                          data-testid={suffixTestId('selectArticle', props)}
                        />
                      </Show>
                    </ButtonGroup>
                  </HStack>
                </DialogFooter>
              </>
            );
          }}
        </Form>
      </DataStatus>
    </>
  );
}
